import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, ArrowRight, History, ShoppingBag, HelpCircle, Plus, Sparkles, LogIn, Lock, Unlock, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

/* ---------- Lock tooltip / Why locked dialog ---------- */
function WhyLockedDialog({ open, onOpenChange, onRecharge }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amethyst-700 to-magenta-700 ring-4 ring-gold-300/50 text-white flex items-center justify-center mb-2 shadow-xl">
            <Lock className="w-7 h-7" />
          </div>
          <DialogTitle className="font-serif text-xl">Why is this Locked?</DialogTitle>
          <DialogDescription className="pt-1">
            <span className="font-semibold text-magenta-700">Shakti Magic Coin</span> is the Official Community Currency. Purchase once. Save every time.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-sm space-y-2.5 text-gray-700 pt-2">
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /><span><b>Extra Bonus Coins</b> with every recharge plan</span></li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /><span><b>Double Gifto Rewards</b> when paying with SMC</span></li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /><span><b>Faster Checkout</b> — no card details needed</span></li>
          <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /><span><b>Premium Member Benefits</b> & festival bonuses</span></li>
        </ul>
        <button
          onClick={() => { onOpenChange(false); onRecharge(); }}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amethyst-700 to-magenta-700 hover:from-amethyst-800 hover:to-magenta-800 text-white font-semibold py-3 shadow-lg shadow-magenta-500/30 active:scale-95 transition-all"
          data-testid="why-locked-recharge-btn"
        >
          💗 Recharge Wallet
        </button>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Unlock celebration (first recharge) — exported for reuse ---------- */
export function UnlockCelebrationDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md text-center">
        <DialogHeader>
          <div className="mx-auto w-20 h-20 rounded-3xl bg-gradient-to-br from-gold-400 via-amethyst-600 to-magenta-700 text-white flex items-center justify-center mb-3 shadow-2xl shadow-magenta-500/40 animate-bounce">
            <Unlock className="w-10 h-10" />
          </div>
          <DialogTitle className="font-serif text-2xl text-center">🎉 Congratulations!</DialogTitle>
          <DialogDescription className="text-center text-base text-amethyst-700 pt-1">
            Your Shakti Wallet is now <span className="font-bold gradient-text-gold">UNLOCKED</span>.
            <br />Welcome to Premium Community Savings.
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================ */

const SECONDARY_COINS = [
  { key: "silver", emoji: "🥈", label: "Silver Shakti Coins", rate: 1,  badge: "Welcome Gift",   tone: "from-slate-100 to-gray-50",    textColor: "text-slate-800",  pillColor: "bg-slate-200/60 text-slate-700" },
  { key: "golden", emoji: "⭐", label: "Golden Shakti Coins", rate: 10, badge: "Welcome Gift",   tone: "from-gold-100 to-amber-50",    textColor: "text-amber-900",  pillColor: "bg-gold-200/60 text-amber-800" },
  { key: "gifto",  emoji: "🎁", label: "Gifto Coins",         rate: 1,  badge: "Delivery Bonus", tone: "from-magenta-100 to-pink-50",  textColor: "text-magenta-800", pillColor: "bg-magenta-200/60 text-magenta-700" },
];

export default function WalletCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWhy, setShowWhy] = useState(false);
  const [bonusEligible, setBonusEligible] = useState(false);
  const [bonusPercent, setBonusPercent] = useState(10);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    Promise.all([
      api.get("/coins/wallet"),
      api.get("/smc/first-recharge-status").catch(() => ({ data: { eligible: false, bonus_percent: 10 } })),
    ])
      .then(([w, fr]) => {
        if (cancelled) return;
        setData(w.data);
        setBonusEligible(!!fr.data?.eligible);
        setBonusPercent(fr.data?.bonus_percent || 10);
      })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const handleRecharge = () => {
    navigate("/wallet/recharge");
  };

  /* ---------------- GUEST STATE ---------------- */
  if (!user) {
    return (
      <section className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6" data-testid="wallet-card-guest">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-[#F5B6C8] border border-[rgba(11,31,58,0.25)] px-5 py-5 sm:px-7 sm:py-6 shadow-xl">
            <div className="relative flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FBE4EB] border border-[rgba(11,31,58,0.25)] text-[#0B1F3A] flex items-center justify-center">
                <Wallet className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#0B1F3A] font-semibold">💗 Shakti Wallet</p>
                <p className="text-base sm:text-lg font-serif font-semibold text-[#0B1F3A] leading-snug">Login to view your Shakti Magic Coins & rewards</p>
                <p className="text-xs text-[#0B1F3A] mt-0.5 hidden sm:block">Official Community Currency · 1 SMC = ₹1</p>
              </div>
              <Link to="/auth" className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0B1F3A] text-white font-semibold text-xs sm:text-sm hover:bg-[#16304F] transition-colors active:scale-95" data-testid="wallet-login-btn">
                <LogIn className="w-3.5 h-3.5" /> Login
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (loading) return <section className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6"><div className="max-w-7xl mx-auto rounded-3xl bg-amethyst-100/40 animate-pulse h-56" /></section>;
  if (!data) return null;

  const smc = data.smc || 0;
  const silver = data.silver || 0;
  const golden = data.golden || 0;
  const gifto = data.gifto || 0;
  const lifetimeRedeemed = data.lifetime_redeemed || 0;
  const isLocked = smc === 0;
  const counts = { silver, golden, gifto };

  return (
    <section className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6" data-testid="wallet-card">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl bg-[#F5B6C8] border border-[rgba(11,31,58,0.25)] shadow-xl">
          {/* Decorative soft sheen blobs (no colour glow) */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/35 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-white/25 blur-3xl pointer-events-none" />

          <div className="relative p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-magenta-700">💗 SHAKTI WALLET</p>
                <p className="text-[10px] uppercase tracking-widest text-gold-700 font-semibold mt-0.5">Official Community Currency</p>
              </div>
              <Link to="/app/wallet" className="text-[11px] sm:text-xs font-semibold text-magenta-700 hover:text-magenta-800 inline-flex items-center gap-0.5" data-testid="wallet-view-full">
                View Wallet <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* ============ SMC PRIMARY HERO — Pink + Navy variant ============ */}
            <div
              className="relative rounded-[28px] p-5 sm:p-6 mb-4 overflow-hidden border-[1.5px] border-[#0B1F3A]/30 shadow-[0_25px_60px_-15px_rgba(11,31,58,0.35)]"
              style={{
                background: "#F5B6C8",
              }}
              data-testid={`smc-hero-${isLocked ? "locked" : "active"}`}
            >
              {/* Soft white sheen for depth (no colour glow) */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.35) 0%, transparent 65%)",
                }}
                aria-hidden="true"
              />
              {/* Premium SMC watermark — large, behind content */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden="true">
                <span className="text-[210px] sm:text-[260px] leading-none opacity-[0.12] drop-shadow-[0_0_30px_rgba(11,31,58,0.25)]">💗</span>
              </div>
              {/* Crisp inner ring */}
              <div className="absolute inset-0 rounded-[28px] pointer-events-none ring-1 ring-inset ring-white/50" aria-hidden="true" />

              <div className="relative flex items-start gap-4">
                {/* Premium Official SMC Coin badge + bilingual tagline */}
                <div className="flex-shrink-0 flex flex-col items-center gap-2">
                  <div className={`w-[82px] h-[82px] sm:w-[104px] sm:h-[104px] rounded-full flex items-center justify-center relative overflow-hidden ${
                    isLocked
                      ? "ring-2 ring-[#0B1F3A]/60 shadow-[0_0_28px_-2px_rgba(11,31,58,0.35)] animate-pulse-slow"
                      : "ring-[3px] ring-[#0B1F3A] shadow-[0_0_32px_-2px_rgba(11,31,58,0.4)]"
                  }`}>
                    {isLocked ? (
                      <button
                        type="button"
                        onClick={() => setShowWhy(true)}
                        aria-label="Why is this locked?"
                        className="relative w-full h-full flex items-center justify-center group focus:outline-none"
                        data-testid="smc-lock-icon"
                      >
                        <img
                          src="/smc-coin.webp"
                          alt="Shakti Magic Coin"
                          className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-[#0B1F3A]/45 backdrop-blur-[1px] rounded-full" aria-hidden="true" />
                        <Lock className="relative w-8 h-8 sm:w-10 sm:h-10 text-[#F5B6C8] drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] group-hover:scale-110 transition-transform" strokeWidth={2} />
                        <span className="absolute -inset-1 rounded-full bg-white/40 blur-md animate-ping-slow opacity-60" aria-hidden="true" />
                      </button>
                    ) : (
                      <div className="relative w-full h-full">
                        <img
                          src="/smc-coin.webp"
                          alt="Shakti Magic Coin — Official Community Currency"
                          className="absolute inset-0 w-full h-full object-cover"
                          data-testid="smc-coin-image"
                        />
                        <span className="absolute -inset-1 rounded-full bg-white/50 blur-lg animate-pulse-slow opacity-50" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  {/* Bilingual official tagline directly below coin */}
                  <div className="text-center max-w-[100px] sm:max-w-[120px]" data-testid="smc-official-tagline">
                    <p className="text-[8px] sm:text-[9px] font-bold text-[#0B1F3A] leading-tight uppercase tracking-[0.1em]">Official Community Currency</p>
                    <p className="text-[8px] sm:text-[9px] font-semibold text-[#0B1F3A]/80 leading-tight mt-0.5" lang="hi">ऑफिशियल कम्युनिटी करेंसी</p>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B1F3A]">💗 Shakti Magic Coin</p>
                    {isLocked ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FBE4EB] border border-[#0B1F3A]/40 text-[#0B1F3A] text-[9px] font-bold uppercase tracking-wider" data-testid="smc-locked-badge">
                        <Lock className="w-2.5 h-2.5" /> Locked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#0B1F3A] border border-[#0B1F3A] text-[#F5B6C8] text-[9px] font-bold uppercase tracking-wider shadow-sm" data-testid="smc-active-badge">
                        ✨ Active Member
                      </span>
                    )}
                  </div>
                  {/* V3 Typography hierarchy — number on top, SMC label below */}
                  <div className="mt-2.5 leading-none">
                    <p className="text-6xl sm:text-7xl font-sans tabular-nums font-extrabold text-[#0B1F3A] tracking-tight" data-testid="smc-balance">{smc.toLocaleString("en-IN")}</p>
                    <p className="mt-1 text-sm font-bold text-[#0B1F3A] tracking-[0.25em] uppercase">SMC</p>
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <p className="text-xs text-[#0B1F3A]/80 uppercase tracking-wider">Shopping Value</p>
                    <p className="text-xl font-sans tabular-nums font-bold text-[#0B1F3A]">₹{smc.toLocaleString("en-IN")}</p>
                  </div>
                  {/* Premium Value-Proposition Cards — 3 prominent feature highlights */}
                  <div className="mt-4 grid grid-cols-3 gap-1.5 sm:gap-2.5">
                    {[
                      { icon: "💗", title: "1 SMC = ₹1",       sub: "Official Shopping Value" },
                      { icon: "✅", title: "100% Redeemable",  sub: "Use on Eligible Purchases" },
                      { icon: "♾️", title: "Never Expires",     sub: "Lifetime Community Value" },
                    ].map((p) => (
                      <div
                        key={p.title}
                        className="relative flex flex-col items-center text-center px-2 py-3 sm:py-3.5 rounded-2xl bg-[#FBE4EB] border border-[#0B1F3A]/20 shadow-[0_4px_12px_-4px_rgba(11,31,58,0.15)] overflow-hidden"
                        data-testid={`smc-feature-${p.title.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                      >
                        <span className="relative text-2xl sm:text-3xl mb-1" aria-hidden="true">{p.icon}</span>
                        <p className="relative text-[11px] sm:text-[14px] font-extrabold text-[#0B1F3A] leading-tight tracking-tight">{p.title}</p>
                        <p className="relative text-[9px] sm:text-[10px] text-[#0B1F3A]/75 font-semibold leading-tight mt-1 px-0.5 break-words">{p.sub}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Locked perks + CTAs */}
              {isLocked ? (
                <div className="relative mt-5">
                  {/* First-Recharge Bonus ribbon — only for eligible users */}
                  {bonusEligible && (
                    <div
                      className="relative mb-4 overflow-hidden rounded-2xl p-3 sm:p-3.5 flex items-center gap-3 border border-[#0B1F3A]/30 shadow-[0_8px_24px_-8px_rgba(11,31,58,0.25)]"
                      style={{ background: "linear-gradient(90deg, #E87A9C 0%, #F5B6C8 50%, #E87A9C 100%)" }}
                      data-testid="first-recharge-banner"
                    >
                      {/* Soft shine sweep */}
                      <span className="absolute inset-y-0 w-1/4 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/70 to-transparent pointer-events-none animate-shine" aria-hidden="true" />
                      <div className="relative flex-shrink-0 w-9 h-9 rounded-full bg-amethyst-900/15 backdrop-blur flex items-center justify-center text-lg">🎁</div>
                      <div className="relative flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amethyst-900/80">Limited Welcome Offer</p>
                        <p className="text-sm sm:text-[15px] font-extrabold text-amethyst-900 leading-tight">
                          First Recharge:&nbsp;
                          <span className="font-sans tabular-nums">{bonusPercent}%</span>
                          &nbsp;Extra SMC FREE
                        </p>
                        <p className="text-[10px] sm:text-[11px] text-amethyst-900/75 leading-snug">Any plan · Auto-applied on your first recharge</p>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-[#0B1F3A] mb-3 flex items-center gap-1.5"><Lock className="w-3 h-3 text-[#0B1F3A]" /> Buy & Unlock Your Wallet</p>
                  {/* Premium capsule chips — wrap-safe layout, fits on any screen */}
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-4">
                    {[
                      { icon: "💎", label: "Premium Savings" },
                      { icon: "⚡", label: "Double Rewards" },
                      { icon: "🎁", label: "Exclusive Offers" },
                    ].map((p) => (
                      <div key={p.label} className="flex flex-col items-center justify-center gap-1 px-1.5 py-2 rounded-2xl bg-[#FBE4EB] border border-[#0B1F3A]/20 shadow-sm min-w-0">
                        <span className="text-base sm:text-lg" aria-hidden="true">{p.icon}</span>
                        <p className="text-[9px] sm:text-[10px] text-[#0B1F3A] font-semibold leading-tight text-center break-words">{p.label}</p>
                      </div>
                    ))}
                  </div>
                  {/* Full-width premium recharge button with shine */}
                  <button
                    type="button"
                    onClick={handleRecharge}
                    className="relative w-full overflow-hidden inline-flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-2xl bg-[#0B1F3A] hover:bg-[#16304F] text-white font-bold text-sm sm:text-base shadow-[0_8px_24px_-4px_rgba(11,31,58,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-transform"
                    data-testid="smc-recharge-unlock-btn"
                  >
                    💗 Recharge &amp; Unlock
                    {/* Shine sweep — fires every ~8s via CSS */}
                    <span className="absolute inset-y-0 w-1/3 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none animate-shine" aria-hidden="true" />
                  </button>
                  {/* Glass secondary button */}
                  <button
                    type="button"
                    onClick={() => setShowWhy(true)}
                    className="mt-2 w-full inline-flex items-center justify-center px-4 py-2.5 rounded-2xl bg-transparent border border-[#0B1F3A]/40 hover:border-[#0B1F3A] hover:bg-[#FBE4EB] text-[#0B1F3A] font-semibold text-xs sm:text-sm transition-all active:scale-[0.98]"
                    data-testid="smc-learn-more-btn"
                  >
                    Learn More
                  </button>
                </div>
              ) : (
                <div className="relative mt-5 flex items-center gap-2 flex-wrap">
                  <button onClick={handleRecharge} className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl bg-[#FBE4EB] hover:bg-[#F5B6C8] text-[#0B1F3A] border border-[#0B1F3A]/30 hover:border-[#0B1F3A]/60 font-semibold text-sm transition-all active:scale-95" data-testid="smc-topup-btn">
                    <span aria-hidden="true">💗</span> Buy SMC
                  </button>
                  <button onClick={() => navigate("/marketplace")} className="relative overflow-hidden flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl bg-[#0B1F3A] hover:bg-[#16304F] text-white font-bold text-sm shadow-[0_8px_24px_-4px_rgba(11,31,58,0.4)] active:scale-95 transition-all" data-testid="smc-use-btn">
                    <ShoppingBag className="w-4 h-4" /> Use Coins
                    <span className="absolute inset-y-0 w-1/3 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none animate-shine" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* ============ SECONDARY COINS ============ */}
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#0B1F3A]/70 mb-2">Your Welcome Gifts & Rewards</p>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {SECONDARY_COINS.map((c) => {
                const balance = counts[c.key];
                const value = balance * c.rate;
                return (
                  <div key={c.key} className={`bg-gradient-to-br ${c.tone} rounded-2xl p-2.5 sm:p-3 border border-white/40 shadow-sm relative`} data-testid={`wallet-coin-${c.key}`}>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-base sm:text-lg leading-none" aria-hidden="true">{c.emoji}</span>
                      <p className={`text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold ${c.textColor} truncate`}>{c.label}</p>
                    </div>
                    <p className={`text-xl sm:text-2xl font-bold ${c.textColor} leading-tight`} data-testid={`wallet-${c.key}-balance`}>{balance.toLocaleString("en-IN")}</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">Value: <b>₹{value.toLocaleString("en-IN")}</b></p>
                    {c.badge && <span className={`mt-1.5 inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${c.pillColor}`}>{c.badge}</span>}
                  </div>
                );
              })}
            </div>

            {/* Lifetime savings — premium white card with purple shadow */}
            <div className="mt-4 rounded-2xl bg-white border border-amethyst-100 px-4 py-3.5 shadow-[0_12px_28px_-12px_rgba(91,35,144,0.35)] flex items-center justify-between gap-3 flex-wrap" data-testid="wallet-stat-lifetime">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-magenta-700 flex items-center gap-1.5"><span aria-hidden="true">💗</span> Lifetime Savings</p>
                <p className="text-2xl sm:text-3xl font-sans tabular-nums font-extrabold text-amethyst-900 leading-none mt-1">₹{lifetimeRedeemed.toLocaleString("en-IN")}</p>
                <p className="text-[11px] text-gray-500 mt-1">You saved with Shakti Magic Coin</p>
              </div>
              <div className="flex items-center gap-1.5">
                <Link to="/app/wallet?tab=history" className="text-[11px] font-semibold text-magenta-700 hover:text-magenta-800 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-magenta-200 hover:bg-magenta-50" data-testid="wallet-history-link">
                  <History className="w-3 h-3" /> History
                </Link>
                <button onClick={() => setShowWhy(true)} className="text-[11px] font-semibold text-amethyst-700 hover:text-amethyst-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-amethyst-200 hover:bg-amethyst-50" data-testid="wallet-howitworks">
                  <HelpCircle className="w-3 h-3" /> How it Works
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WhyLockedDialog open={showWhy} onOpenChange={setShowWhy} onRecharge={handleRecharge} />
    </section>
  );
}
